import {observer} from 'mobx-react-lite';
import type {Insets, TextProps} from 'react-native';
import {Pressable, StyleSheet, Text, View} from 'react-native';

import CalendarSvg from '../../../assets/calendar.svg';
import ClockSvg from '../../../assets/clock.svg';
import NotificationOutlineSvg from '../../../assets/notification_outline.svg';
import {useTheme, variance} from '../../styling';

export type GroupListItemViewProps = {
  name: string;
  address: string;
  upcomingEvent: {day: string; start: string} | null;
  showNotificationButton?: boolean;
  onNotificationsPress?: () => void;
};

export default observer(function GroupListItemView(
  props: GroupListItemViewProps,
) {
  const {
    name,
    address,
    upcomingEvent,
    onNotificationsPress,
    showNotificationButton,
  } = props;
  const theme = useTheme();
  return (
    <GroupRootView>
      <View style={layoutStyles.row}>
        <GroupNameText style={layoutStyles.shrink}>{name}</GroupNameText>
        {showNotificationButton && (
          <Pressable
            style={layoutStyles.noShrink}
            onPress={onNotificationsPress}
            hitSlop={HIT_SLOP}>
            <NotificationOutlineSvg color={theme.palette.textPrimary} />
          </Pressable>
        )}
      </View>
      <GroupAddressText>{address}</GroupAddressText>
      <View style={[layoutStyles.row, layoutStyles.marginTop]}>
        <View style={layoutStyles.noShrink}>
          <CalendarSvg color={theme.palette.textPrimary} />
        </View>
        {upcomingEvent ? (
          <>
            <GroupTimeText style={layoutStyles.shrink}>
              {upcomingEvent.day}
            </GroupTimeText>
            <View style={layoutStyles.noShrink}>
              <ClockSvg color={theme.palette.textPrimary} />
            </View>
            <GroupTimeText style={layoutStyles.noShrink}>
              {upcomingEvent.start}
            </GroupTimeText>
          </>
        ) : (
          <GroupTimeText style={layoutStyles.shrink}>
            Нет собраний
          </GroupTimeText>
        )}
      </View>
    </GroupRootView>
  );
});

const HIT_SLOP = {top: 16, left: 16, bottom: 16, right: 16} satisfies Insets;

const layoutStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: 8,
  },
  noShrink: {
    flexShrink: 0,
    flexGrow: 0,
  },
  shrink: {
    flexShrink: 1,
    flexGrow: 1,
  },
  marginTop: {
    marginTop: 12,
  },
});

const GroupRootView = variance(View)(theme => ({
  root: {
    alignItems: 'stretch',
    borderRadius: 11,
    paddingHorizontal: 20,
    paddingVertical: 12,
    background: theme.palette.backgroundAverage,
  },
}));

const GroupNameText = variance(Text)(
  theme => ({
    root: {
      ...theme.text('header6'),
      color: theme.palette.textPrimary,
      fontSize: 20,
      lineHeight: 35,
    },
  }),
  (): TextProps => ({
    numberOfLines: 1,
    ellipsizeMode: 'tail',
  }),
);

const GroupAddressText = variance(Text)(
  theme => ({
    root: {
      ...theme.text('base'),
      color: theme.palette.textPrimary,
      fontSize: 16,
      lineHeight: 18,
    },
  }),
  (): TextProps => ({
    numberOfLines: 3,
    ellipsizeMode: 'tail',
  }),
);

const GroupTimeText = variance(Text)(
  theme => ({
    root: {
      ...theme.text('header6'),
      color: theme.palette.textPrimary,
      fontSize: 16,
      lineHeight: 18,
    },
  }),
  (): TextProps => ({
    numberOfLines: 1,
    ellipsizeMode: 'middle',
  }),
);
